import type { ReactNode } from 'react';
import { cn } from '@/lib/cn';
import { formatNumber } from '@/lib/format';

export type MeterTone =
  | 'primary'
  | 'accent'
  | 'info'
  | 'negative'
  | 'success'
  /** Product-area fills: AI & data (blue), messages & customers (sea-teal). */
  | 'ai'
  | 'messages';
const fills: Record<MeterTone, string> = {
  primary: 'bg-primary',
  ai: 'bg-area-ai',
  messages: 'bg-area-messages',
  accent: 'bg-accent',
  info: 'bg-info',
  negative: 'bg-negative',
  success: 'bg-success',
};

/**
 * Labelled meter (credits, quotas, progress). Turns red when remaining ≤ `warnAt` of max.
 * `value` = remaining/used per `mode`.
 */
export function ProgressMeter({
  label,
  value,
  max,
  tone = 'primary',
  mode = 'remaining',
  warnAt = 0.15,
  caption,
  size = 'md',
  className,
  hideNumbers,
  showMax = true,
}: {
  label?: ReactNode;
  value: number;
  max: number;
  tone?: MeterTone;
  mode?: 'remaining' | 'used';
  warnAt?: number;
  caption?: ReactNode;
  size?: 'xs' | 'sm' | 'md';
  className?: string;
  hideNumbers?: boolean;
  /** Show the "/ max" denominator (hide it when the balance exceeds the allowance). */
  showMax?: boolean;
}) {
  const ratio = max > 0 ? Math.min(1, Math.max(0, value / max)) : 0;
  const remainingRatio = mode === 'remaining' ? ratio : 1 - ratio;
  const low = max > 0 && remainingRatio <= warnAt;
  return (
    <div className={cn('grid gap-1.5', className)}>
      {(label || !hideNumbers) && (
        <div className="flex items-baseline justify-between gap-3 text-sm">
          {label && <span className="text-ink-soft">{label}</span>}
          {!hideNumbers && (
            <span className="num text-ink-muted">
              <span className={cn('font-extrabold', low ? 'text-negative-ink' : 'text-ink')}>
                {formatNumber(value)}
              </span>
              {showMax && <span className="text-ink-subtle"> / {formatNumber(max)}</span>}
            </span>
          )}
        </div>
      )}
      <div
        role="meter"
        aria-valuemin={0}
        aria-valuemax={max}
        aria-valuenow={value}
        aria-label={typeof label === 'string' ? label : undefined}
        className={cn(
          'relative overflow-hidden rounded-full bg-sunken',
          size === 'xs' ? 'h-1' : size === 'sm' ? 'h-1.5' : 'h-2',
        )}
      >
        <div
          className={cn(
            'h-full rounded-full transition-[width] duration-700 ease-out',
            low ? 'bg-negative' : fills[tone],
          )}
          style={{ width: `${ratio * 100}%` }}
        />
      </div>
      {caption && <div className="text-xs text-ink-muted">{caption}</div>}
    </div>
  );
}
